import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getApplicationAllotmentDetailsID,
  fetchNumberOfDesk,
  fetchScrutinyLog,
  fetchAllotmentStatus
} from "../api/fetch-scheme";
const WarningIcon = () => (
  <svg
    aria-hidden="true"
    className="application-status-warning-icon"
    viewBox="0 0 24 24"
  >
    <path
      d="M12 3.75 21 19.5H3L12 3.75Zm0 5.25a.75.75 0 0 0-.75.75v4.5a.75.75 0 0 0 1.5 0v-4.5A.75.75 0 0 0 12 9Zm0 8.25a.938.938 0 1 0 0-1.875.938.938 0 0 0 0 1.875Z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg aria-hidden="true" className="application-status-step-icon" viewBox="0 0 24 24">
    <path
      d="m9.55 16.6-4.2-4.2 1.4-1.4 2.8 2.8 7-7 1.4 1.4-8.4 8.4Z"
      fill="currentColor"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg aria-hidden="true" className="application-status-step-icon" viewBox="0 0 24 24">
    <path
      d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7a5 5 0 1 1-5 5H5a7 7 0 1 0 12.65-5.65Z"
      fill="currentColor"
    />
  </svg>
);

const formatDateTime = (value) => {
  if (!value) {
    return "2-Aug-2025, 9:35 AM";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "2-Aug-2025, 9:35 AM";
  }

  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getSchemeNameLabel = (label) => {
  if (!label) {
    return "";
  }

  if (typeof label === "string") {
    return label;
  }

  return label.en_US || label.en_IN || Object.values(label)[0] || "";
};

const getTaskName = (task) => {
  const rawLabel = task?.name || task?.deskName || task?.label || "";

  return String(rawLabel).replace(/\s+/g, " ").trim();
};

const getDeskLevelIndex = (value) => {
  const match = String(value || "").match(/Level\s*(\d+)/i);

  if (!match) {
    return -1;
  }

  return Math.max(Number(match[1]) - 1, 0);
};

const getDeskLevelNumber = (value) => {
  const deskIndex = getDeskLevelIndex(value);

  return deskIndex >= 0 ? deskIndex + 1 : 0;
};

const getDeskActionCode = (value) =>
  String(value?.sendBackReferenece ?? "").trim();

const getDeskActionDate = (value) =>
  formatDateTime(value?.dateModified || value?.dateCreated);

const getScrutinyCompletionDeskNumber = (totalDesk = 0) => {
  const normalizedTotalDesk = Number(totalDesk || 0);

  if (normalizedTotalDesk <= 0) {
    return 0;
  }

  return Math.max(normalizedTotalDesk - 1, 1);
};

const getDeskStatusConfig = (deskIndex, actionCode) => {
  if (actionCode === "0") {
    return {
      status: Liferay.Language.get("approved"),
      variant: "completed",
    };
  }

  if (actionCode === "1") {
    return {
      status: Liferay.Language.get("rejected"),
      variant: "rejected",
    };
  }

  if (actionCode === "2") {
    if (deskIndex === 0) {
      return {
        status: Liferay.Language.get("pending-at-applicant"),
        variant: "applicant",
      };
    }

    return {
      status: Liferay.Language.get("send-back-to-desk-x").replace(
        "{0}",
        String(deskIndex)
      ),
      variant: "send-back",
    };
  }

  return {
    status: Liferay.Language.get("not-started"),
    variant: "pending",
  };
};

const getLatestEntry = (entries = []) =>
  [...entries].sort((firstEntry, secondEntry) => {
    const firstDate = new Date(
      firstEntry?.dateModified || firstEntry?.dateCreated || 0
    ).getTime();
    const secondDate = new Date(
      secondEntry?.dateModified || secondEntry?.dateCreated || 0
    ).getTime();

    return secondDate - firstDate;
  })[0] || null;

const getEntryTime = (entry) =>
  new Date(entry?.dateModified || entry?.dateCreated || 0).getTime();

const getLatestDeskEntry = (entries = [], deskNumber = 0) =>
  getLatestEntry(
    entries.filter(
      (entry) => getDeskLevelNumber(entry?.scrutinyLevel) === Number(deskNumber || 0)
    )
  );

const sortEntriesByActionTime = (entries = []) =>
  [...entries].sort((firstEntry, secondEntry) => {
    const timeDifference =
      getEntryTime(firstEntry) - getEntryTime(secondEntry);

    if (timeDifference !== 0) {
      return timeDifference;
    }

    return Number(firstEntry?.id || 0) - Number(secondEntry?.id || 0);
  });

const isScrutinyCompleted = (entries = [], totalDesk = 0) => {
  const scrutinyCompletionDeskNumber =
    getScrutinyCompletionDeskNumber(totalDesk);
  const scrutinyCompletionEntry = getLatestDeskEntry(
    entries,
    scrutinyCompletionDeskNumber
  );
  const latestActionCode = getDeskActionCode(scrutinyCompletionEntry);

  return (
    latestActionCode === "0" &&
    totalDesk > 0
  );
};

function ApplicationStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const application = useMemo(
    () => location.state?.application || {},
    [location.state]
  );
  const objectInfo = useMemo(
    () => location.state?.objectInfo || {},
    [location.state]
  );
  const schemeconfiguratorId = useMemo(
    () => location.state?.schemeconfiguratorId || "",
    [location.state]
  );
  const [resAllotment, setResAllotment] = useState(null);
  const [workflowDeskInfo, setWorkflowDeskInfo] = useState(null);
  const [scrutinyLog, setScrutinyLog] = useState([]);
  const [allotmentStatus, setAllotmentStatus] = useState([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [statusError, setStatusError] = useState("");

  const applicantName =
    application?.name ||
    application?.farmername ||
    application?.applicantfullname ||
    application?.fullname ||
    "";

  const applicationId =
    application?.applicationReferenceNumber ||
    application?.applicationreferencenumber ||
    application?.applicationNo ||
    application?.applicationId ||
    application?.id ||
    Liferay.Language.get("not-available");

  const schemeName = getSchemeNameLabel(objectInfo?.label);

  const submissionDate = formatDateTime(
    application?.dateCreated || application?.createdDate || application?.submittedDate
  );

  const applicationTimeline = String(
    resAllotment?.applicationTimelines || ""
  ).trim();

  console.log("applicationTimeline:::",applicationTimeline)
  const sortedScrutinyLog = useMemo(
    () => sortEntriesByActionTime(scrutinyLog),
    [scrutinyLog]
  );
  const statusSummary = useMemo(() => {
    const totalDesk = Number(workflowDeskInfo?.totalDesk || 0);
    const scrutinyCompletionDeskNumber =
      getScrutinyCompletionDeskNumber(totalDesk);
    const latestEntry = getLatestEntry(sortedScrutinyLog);
    const scrutinyCompletionEntry = getLatestDeskEntry(
      sortedScrutinyLog,
      scrutinyCompletionDeskNumber
    );
    const latestAllotmentEntry = getLatestEntry(allotmentStatus);
    const latestDeskIndex = getDeskLevelIndex(
      latestEntry?.scrutinyLevel
    );
    const latestActionCode = getDeskActionCode(latestEntry);
    const hasScrutinyCompleted =
      totalDesk > 0 &&
      getDeskActionCode(scrutinyCompletionEntry) === "0";
    const allotmentApplicationState =
      latestAllotmentEntry?.submittedStatus;
    const isDisbursementApproved =
      hasScrutinyCompleted &&
      allotmentStatus.length > 0 &&
      allotmentApplicationState === "XML Generated";
    const scrutinyCompletedDate = hasScrutinyCompleted
      ? getDeskActionDate(scrutinyCompletionEntry)
      : "";
    const disbursementCompletedDate = isDisbursementApproved
      ? getDeskActionDate(latestAllotmentEntry)
      : "";

    if (!sortedScrutinyLog.length) {
      return {
        message: Liferay.Language.get("under-scrutiny"),
        badgeVariant: "warning",
        progressScale: 0.5,
        scrutinyState: "active",
        disbursementState: "pending",
        scrutinyDate: "",
        disbursementDate: "",
      };
    }

    if (latestActionCode === "1") {
      return {
        message: Liferay.Language.get("rejected"),
        badgeVariant: "danger",
        progressScale: 0.5,
        scrutinyState: "failed",
        disbursementState: "pending",
        scrutinyDate: "",
        disbursementDate: "",
      };
    }

    if (latestActionCode === "2") {
      return {
        message:
          latestDeskIndex === 0
            ? Liferay.Language.get("pending-at-applicant")
            : Liferay.Language.get("send-back-to-desk-x").replace(
                "{0}",
                String(latestDeskIndex)
              ),
        badgeVariant: "warning",
        progressScale: 0.5,
        scrutinyState: "active",
        disbursementState: "pending",
        scrutinyDate: "",
        disbursementDate: "",
      };
    }

    if (hasScrutinyCompleted) {
      if (isDisbursementApproved) {
        return {
          message: Liferay.Language.get("approved"),
          badgeVariant: "success",
          progressScale: 1,
          scrutinyState: "done",
          disbursementState: "done",
          scrutinyDate: scrutinyCompletedDate,
          disbursementDate: disbursementCompletedDate,
        };
      }

      return {
        message: Liferay.Language.get("under-disbursement"),
        badgeVariant: "warning",
        progressScale: 1,
        scrutinyState: "done",
        disbursementState: "active",
        scrutinyDate: scrutinyCompletedDate,
        disbursementDate: "",
      };
    }

    return {
      message: Liferay.Language.get("under-scrutiny"),
      badgeVariant: "warning",
      progressScale: 0.5,
      scrutinyState: "active",
      disbursementState: "pending",
      scrutinyDate: "",
      disbursementDate: "",
    };
  }, [allotmentStatus, sortedScrutinyLog, workflowDeskInfo]);
  const currentStatusMessage = statusSummary.message;
  const badgeVariant = statusSummary.badgeVariant;

  const steps = useMemo(
    () => [
      {
        key: "submission",
        title: Liferay.Language.get("application-submission"),
        date: submissionDate,
        state: "done",
      },
      {
        key: "scrutiny",
        title: Liferay.Language.get("scrutiny"),
        date: statusSummary.scrutinyDate,
        state: statusSummary.scrutinyState,
      },
      {
        key: "disbursement",
        title: Liferay.Language.get("disbursement"),
        date: statusSummary.disbursementDate,
        state: statusSummary.disbursementState,
      },
    ],
    [statusSummary, submissionDate]
  );

  const progressSegments = useMemo(() => {
    return steps.slice(1).map((step, index) => ({
      key: `${index}-${step.key}`,
      variant:
        step.state === "done"
          ? "completed"
          : step.state === "active"
            ? "active"
          : step.state === "failed"
            ? "failed"
            : "pending",
    }));
  }, [steps]);

  const desks = useMemo(() => {
    const workflowTasks = workflowDeskInfo?.tasks || [];
    const deskStates = workflowTasks.map((task, index) => ({
      key: `desk-${index + 1}`,
      title: Liferay.Language.get("desk-x").replace("{0}", String(index + 1)),
      taskName: getTaskName(task),
      status: Liferay.Language.get("not-started"),
      date: "",
      variant: "pending",
    }));
    const latestDeskEntries = new Map();

    sortedScrutinyLog.forEach((entry) => {
      const deskIndex = getDeskLevelIndex(entry?.scrutinyLevel);

      if (deskIndex < 0 || !deskStates[deskIndex]) {
        return;
      }

      latestDeskEntries.set(deskIndex, entry);
    });

    latestDeskEntries.forEach((entry, deskIndex) => {
      const actionCode = getDeskActionCode(entry);
      const statusConfig = getDeskStatusConfig(deskIndex, actionCode);

      deskStates[deskIndex].status = statusConfig.status;
      deskStates[deskIndex].variant = statusConfig.variant;
      deskStates[deskIndex].date = getDeskActionDate(entry);
    });

    const latestScrutinyEntry = getLatestEntry(sortedScrutinyLog);
    const latestDeskIndex = getDeskLevelIndex(
      latestScrutinyEntry?.scrutinyLevel
    );
    const latestActionCode = getDeskActionCode(latestScrutinyEntry);

    if (!sortedScrutinyLog.length && deskStates.length) {
      deskStates[0].status = Liferay.Language.get("in-progress");
      deskStates[0].variant = "in-progress";
    } else if (latestActionCode === "0") {
      const nextDesk = deskStates[latestDeskIndex + 1];

      if (nextDesk && nextDesk.variant === "pending") {
        nextDesk.status = Liferay.Language.get("in-progress");
        nextDesk.variant = "in-progress";
      }
    }

    return deskStates;
  }, [sortedScrutinyLog, workflowDeskInfo]);

  const deskLogs = useMemo(() => {
    const workflowTasks = workflowDeskInfo?.tasks || [];

    return sortedScrutinyLog.reduce((result, entry, entryIndex) => {
      const deskIndex = getDeskLevelIndex(entry?.scrutinyLevel);

      if (deskIndex < 0) {
        return result;
      }

      const statusConfig = getDeskStatusConfig(
        deskIndex,
        getDeskActionCode(entry)
      );

      result.push({
        key: entry?.id || `${deskIndex}-${entryIndex}`,
        desk: Liferay.Language.get("desk-x").replace(
          "{0}",
          String(deskIndex + 1)
        ),
        taskName: getTaskName(workflowTasks[deskIndex]),
        status: statusConfig.status,
        variant: statusConfig.variant,
        date: getDeskActionDate(entry),
      });

      return result;
    }, []);
  }, [sortedScrutinyLog, workflowDeskInfo]);

useEffect(() => {

  if (!application?.id ||!schemeconfiguratorId) {
    return;
  }

  const getApplicationStatus = async () => {
    setIsStatusLoading(true);
    setLoadingMessage(
      Liferay.Language.get("loading-application-details-please-wait")
    );
    setStatusError("");
    setWorkflowDeskInfo(null);
    setScrutinyLog([]);
    setAllotmentStatus([]);

    try {
      const resAllotment = await getApplicationAllotmentDetailsID(
        schemeconfiguratorId?.split("_")[0]
      );

      setResAllotment(resAllotment || null);
      console.log("resAllotment:::::::",resAllotment)

      if (!resAllotment) {
        setStatusError(
          Liferay.Language.get("unable-to-load-application-allotment-details")
        );
        return;
      }
      setLoadingMessage(Liferay.Language.get("loading-current-application-status"));
      const noOfDesk = await fetchNumberOfDesk(resAllotment?.selectedWorkflow);
      setWorkflowDeskInfo(noOfDesk || null);
      const scrutinyLogRes = await fetchScrutinyLog(
          application?.id
      );
      setScrutinyLog(Array.isArray(scrutinyLogRes) ? scrutinyLogRes : []);

      if (
        isScrutinyCompleted(
          Array.isArray(scrutinyLogRes) ? scrutinyLogRes : [],
          Number(noOfDesk?.totalDesk || 0)
        )
      ) {
        setLoadingMessage(Liferay.Language.get("loading-disbursement-status"));
        const allotmentRes = await fetchAllotmentStatus(
          resAllotment?.applicationAllotmentDetailsID,
          application?.applicationreferencenumber ||
            application?.applicationReferenceNumber
        );
        setAllotmentStatus(Array.isArray(allotmentRes) ? allotmentRes : []);
      }

      console.log("noOfDesk:::::::",noOfDesk," scrutinyLogRes:::::",scrutinyLogRes)
    } catch (error) {
      console.error("Error loading application status view:", error);
      setStatusError(Liferay.Language.get("unable-to-load-application-details"));
    } finally {
      setLoadingMessage("");
      setIsStatusLoading(false);
    }
  };

  getApplicationStatus();

}, [application, schemeconfiguratorId]);

  if (isStatusLoading) {
    return (
      <div className="container py-3 py-lg-4">
        <div className="modal-backdrop fade show"></div>
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "420px", width: "100%" }}
          >
            <div className="modal-content shadow-lg border-0 rounded-3 overflow-hidden">
              <div
                className="modal-header text-white py-2 px-3"
                style={{ background: "#12397f" }}
              >
                <h5 className="modal-title fw-bold mb-0 fs-6">
                  {Liferay.Language.get("loading-application-status")}
                </h5>
              </div>
              <div
                className="modal-body p-4 text-center"
                style={{ background: "#f8f9fc" }}
              >
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                  aria-hidden="true"
                />
                <p className="application-status-loading-text mb-0">
                  {loadingMessage ||
                    Liferay.Language.get(
                      "please-wait-while-we-load-your-application-details"
                    )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-3 py-lg-4">
      <div className="application-status-wrapper">
        <div className="application-status-top-right">
          <div className="d-flex align-items-center justify-content-end">
            {/* <div
              className={`application-status-warning-icon application-status-warning-icon-${badgeVariant}`}
            >
              <WarningIcon />
            </div> */}
            <div
              className={`application-status-badge application-status-badge-${badgeVariant}`}
            >
              {isStatusLoading
                ? Liferay.Language.get("loading-status")
                : currentStatusMessage}
            </div>
          </div>

          {applicationTimeline ? (
            <div className="application-status-timeline">
              {Liferay.Language.get("timeline-for-providing-service")}{" "}
              <strong>{applicationTimeline}</strong>
            </div>
          ) : null}
        </div>

        <div className="application-status-heading">
          {Liferay.Language.get("application-status")}
        </div>

        <div className="application-status-subheading">
          {Liferay.Language.get("hi")} <strong>{applicantName}</strong>,{" "}
          {Liferay.Language.get("below-is-the-status-of-your-application")}{" "}
          <strong>{applicationId}</strong> {Liferay.Language.get("for")}{" "}
          <strong>{schemeName}</strong>.
        </div>
        {statusError ? (
          <div className="track-field-error mb-3">{statusError}</div>
        ) : null}

        <div className="application-status-progress">
          <div className="application-status-progress-line" aria-hidden="true">
            {progressSegments.map((segment) => (
              <span
                key={segment.key}
                className={`application-status-progress-line-segment application-status-progress-line-segment-${segment.variant}`}
              />
            ))}
          </div>

          {steps.map((step) => (
            <div key={step.key} className="application-status-step-box">
              <div
                className={`application-status-step-circle ${
                  step.state === "done"
                    ? "application-status-step-green"
                    : step.state === "active"
                      ? "application-status-step-yellow"
                      : step.state === "failed"
                        ? "application-status-step-red"
                        : "application-status-step-gray"
                }`}
              >
                {step.state === "done" ? (
                  <CheckIcon />
                ) : step.state === "active" ? (
                  <RefreshIcon />
                ) : step.state === "failed" ? (
                  <WarningIcon />
                ) : null}
              </div>
              <div className="application-status-step-title">{step.title}</div>
              {step.date ? (
                <div className="application-status-step-date">{step.date}</div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="application-status-desk-container">
          <div className="application-status-desk-scroll">
            {desks.length ? (
              desks.map((desk) => (
                <div
                  key={desk.key}
                  className={`application-status-desk-item application-status-desk-item-${desk.variant}`}
                >
                  <div className="application-status-desk-meta">
                    <div className="application-status-desk-title">{desk.title}</div>
                    {desk.taskName ? (
                      <div className="application-status-desk-label">{desk.taskName}</div>
                    ) : null}
                    {desk.date ? (
                      <div className="application-status-desk-date">{desk.date}</div>
                    ) : null}
                  </div>
                  {desk.status ? (
                    <div
                      className={`application-status-desk-state application-status-desk-state-${desk.variant}`}
                    >
                      {desk.status}
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="application-status-desk-empty">
                {Liferay.Language.get(
                  "no-workflow-desks-available-for-this-application"
                )}
              </div>
            )}
          </div>
        </div>

        {/* {deskLogs.length ? (
          <div className="application-status-log-card">
            <div className="application-status-log-title">
              {Liferay.Language.get("track-status")}
            </div>
            <div className="application-status-log-table-wrap">
              <table className="application-status-log-table">
                <thead>
                  <tr>
                    <th>{Liferay.Language.get("desk")}</th>
                    <th>{Liferay.Language.get("desk-name")}</th>
                    <th>{Liferay.Language.get("status")}</th>
                    <th>{Liferay.Language.get("date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deskLogs.map((log) => (
                    <tr key={log.key}>
                      <td>{log.desk}</td>
                      <td>{log.taskName || "-"}</td>
                      <td
                        className={`application-status-log-status application-status-log-status-${log.variant}`}
                      >
                        {log.status}
                      </td>
                      <td>{log.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null} */}

        <button
          type="button"
          className="application-status-close-btn"
          onClick={() => navigate("/track-application")}
        >
          {Liferay.Language.get("close")}
        </button>
      </div>
    </div>
  );
}

export default ApplicationStatus;
